import { Component } from '@theme/component';

/**
 * Hover/focus dropdown menu for the announcement bar secondary header.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - Submenu panels.
 *
 * @extends {Component<Refs>}
 */
class SecondaryHeaderMenu extends Component {
  /**
   * @type {{ activeItem: HTMLElement | null }}
   */
  #state = {
    activeItem: null,
  };

  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item || item === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);
    if (submenu) {
      submenu.dataset.active = '';
    }
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const listItem = this.#state.activeItem?.closest('.menu-list__list-item');
    const isMovingWithinListItem =
      event.relatedTarget instanceof Node && listItem?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);

    if (isMovingWithinListItem || isMovingToSubmenu) return;

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    const submenu = findSubmenu(item);
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };
}

if (!customElements.get('secondary-header-menu')) {
  customElements.define('secondary-header-menu', SecondaryHeaderMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) {
    return element;
  }

  const link = element.querySelector('[ref="menuitem"]') ?? element.closest('[ref="menuitem"]');
  return link instanceof HTMLElement ? link : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;
  const listItem = element.closest('.menu-list__list-item');
  const submenu = listItem?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
